// ============================================================================
// COLLISION SYSTEM - Spatial Partitioning and Detection
// ============================================================================

import { Vector2, Rectangle, EntityId } from '@/shared/types';
import { Vec2, Collision } from '@/shared/utils';

// ----------------------------------------------------------------------------
// Collider Types
// ----------------------------------------------------------------------------

export enum ColliderType {
  Circle = 'circle',
  Box = 'box',
}

export interface CircleCollider {
  type: ColliderType.Circle;
  offset: Vector2;
  radius: number;
}

export interface BoxCollider {
  type: ColliderType.Box;
  offset: Vector2;
  width: number;
  height: number;
}

export type Collider = CircleCollider | BoxCollider;

export interface CollisionBody {
  id: EntityId;
  position: Vector2;
  velocity: Vector2;
  collider: Collider;
  layer: number;       // What layer this body is on
  mask: number;        // What layers this body collides with
  isStatic: boolean;
  isTrigger: boolean;  // Don't resolve collision, just detect
  userData?: unknown;
}

export interface CollisionResult {
  bodyA: CollisionBody;
  bodyB: CollisionBody;
  normal: Vector2;
  depth: number;
  point: Vector2;
}

// ----------------------------------------------------------------------------
// Collision Layers
// ----------------------------------------------------------------------------

export const CollisionLayers = {
  NONE: 0,
  PLAYER: 1 << 0,
  ENEMY: 1 << 1,
  PROJECTILE: 1 << 2,
  WALL: 1 << 3,
  TRIGGER: 1 << 4,
  PICKUP: 1 << 5,
  ALL: 0xFFFFFFFF,
} as const;

// ----------------------------------------------------------------------------
// Spatial Hash Grid
// ----------------------------------------------------------------------------

export class SpatialHashGrid {
  private cellSize: number;
  private cells = new Map<string, Set<EntityId>>();
  private bodyPositions = new Map<EntityId, string[]>();

  constructor(cellSize = 128) {
    this.cellSize = cellSize;
  }

  private getCellsForBody(body: CollisionBody): string[] {
    const bounds = this.getBodyBounds(body);
    const keys: string[] = [];

    const minCX = Math.floor(bounds.x / this.cellSize);
    const minCY = Math.floor(bounds.y / this.cellSize);
    const maxCX = Math.floor((bounds.x + bounds.width) / this.cellSize);
    const maxCY = Math.floor((bounds.y + bounds.height) / this.cellSize);

    for (let cx = minCX; cx <= maxCX; cx++) {
      for (let cy = minCY; cy <= maxCY; cy++) {
        keys.push(`${cx},${cy}`);
      }
    }

    return keys;
  }

  private getBodyBounds(body: CollisionBody): Rectangle {
    if (body.collider.type === ColliderType.Circle) {
      const c = body.collider;
      return {
        x: body.position.x + c.offset.x - c.radius,
        y: body.position.y + c.offset.y - c.radius,
        width: c.radius * 2,
        height: c.radius * 2,
      };
    } else {
      const c = body.collider;
      return {
        x: body.position.x + c.offset.x,
        y: body.position.y + c.offset.y,
        width: c.width,
        height: c.height,
      };
    }
  }

  insert(body: CollisionBody): void {
    this.remove(body.id);

    const cellKeys = this.getCellsForBody(body);
    this.bodyPositions.set(body.id, cellKeys);

    for (const key of cellKeys) {
      if (!this.cells.has(key)) {
        this.cells.set(key, new Set());
      }
      this.cells.get(key)!.add(body.id);
    }
  }

  remove(id: EntityId): void {
    const cellKeys = this.bodyPositions.get(id);
    if (cellKeys) {
      for (const key of cellKeys) {
        this.cells.get(key)?.delete(id);
      }
      this.bodyPositions.delete(id);
    }
  }

  update(body: CollisionBody): void {
    this.insert(body);
  }

  query(bounds: Rectangle): Set<EntityId> {
    const result = new Set<EntityId>();

    const minCX = Math.floor(bounds.x / this.cellSize);
    const minCY = Math.floor(bounds.y / this.cellSize);
    const maxCX = Math.floor((bounds.x + bounds.width) / this.cellSize);
    const maxCY = Math.floor((bounds.y + bounds.height) / this.cellSize);

    for (let cx = minCX; cx <= maxCX; cx++) {
      for (let cy = minCY; cy <= maxCY; cy++) {
        const cell = this.cells.get(`${cx},${cy}`);
        if (cell) {
          for (const id of cell) {
            result.add(id);
          }
        }
      }
    }

    return result;
  }

  getNearby(body: CollisionBody): Set<EntityId> {
    const bounds = this.getBodyBounds(body);
    // Expand bounds slightly
    bounds.x -= this.cellSize;
    bounds.y -= this.cellSize;
    bounds.width += this.cellSize * 2;
    bounds.height += this.cellSize * 2;

    const nearby = this.query(bounds);
    nearby.delete(body.id);
    return nearby;
  }

  clear(): void {
    this.cells.clear();
    this.bodyPositions.clear();
  }
}

// ----------------------------------------------------------------------------
// Collision World
// ----------------------------------------------------------------------------

export class CollisionWorld {
  private bodies = new Map<EntityId, CollisionBody>();
  private spatialHash: SpatialHashGrid;
  private collisions: CollisionResult[] = [];

  constructor(cellSize = 128) {
    this.spatialHash = new SpatialHashGrid(cellSize);
  }

  addBody(body: CollisionBody): void {
    this.bodies.set(body.id, body);
    this.spatialHash.insert(body);
  }

  removeBody(id: EntityId): void {
    this.bodies.delete(id);
    this.spatialHash.remove(id);
  }

  getBody(id: EntityId): CollisionBody | undefined {
    return this.bodies.get(id);
  }

  updateBody(id: EntityId, position: Vector2, velocity?: Vector2): void {
    const body = this.bodies.get(id);
    if (body) {
      body.position = position;
      if (velocity) body.velocity = velocity;
      this.spatialHash.update(body);
    }
  }

  detectCollisions(): CollisionResult[] {
    this.collisions = [];

    for (const [id, bodyA] of this.bodies) {
      const nearby = this.spatialHash.getNearby(bodyA);

      for (const otherId of nearby) {
        // Avoid duplicate checks
        if (otherId <= id) continue;

        const bodyB = this.bodies.get(otherId);
        if (!bodyB) continue;

        // Check layer masks
        if ((bodyA.layer & bodyB.mask) === 0 && (bodyB.layer & bodyA.mask) === 0) {
          continue;
        }

        // Check collision
        const result = this.checkCollision(bodyA, bodyB);
        if (result) {
          this.collisions.push(result);
        }
      }
    }

    return this.collisions;
  }

  private checkCollision(bodyA: CollisionBody, bodyB: CollisionBody): CollisionResult | null {
    const colliderA = bodyA.collider;
    const colliderB = bodyB.collider;

    const posA = Vec2.add(bodyA.position, colliderA.offset);
    const posB = Vec2.add(bodyB.position, colliderB.offset);

    // Circle vs Circle
    if (colliderA.type === ColliderType.Circle && colliderB.type === ColliderType.Circle) {
      const radiusSum = colliderA.radius + colliderB.radius;
      const dist = Vec2.distance(posA, posB);

      if (dist < radiusSum) {
        const normal = Vec2.normalize(Vec2.sub(posB, posA));
        const depth = radiusSum - dist;
        const point = Vec2.add(posA, Vec2.mul(normal, colliderA.radius));

        return { bodyA, bodyB, normal, depth, point };
      }
    }

    // Box vs Box
    if (colliderA.type === ColliderType.Box && colliderB.type === ColliderType.Box) {
      const rectA: Rectangle = { x: posA.x, y: posA.y, width: colliderA.width, height: colliderA.height };
      const rectB: Rectangle = { x: posB.x, y: posB.y, width: colliderB.width, height: colliderB.height };

      if (Collision.rectIntersect(rectA, rectB)) {
        // Calculate overlap and normal (simplified)
        const overlapX = Math.min(rectA.x + rectA.width, rectB.x + rectB.width) - Math.max(rectA.x, rectB.x);
        const overlapY = Math.min(rectA.y + rectA.height, rectB.y + rectB.height) - Math.max(rectA.y, rectB.y);

        const centerA = { x: posA.x + colliderA.width / 2, y: posA.y + colliderA.height / 2 };
        const centerB = { x: posB.x + colliderB.width / 2, y: posB.y + colliderB.height / 2 };
        const diff = Vec2.sub(centerB, centerA);

        let normal: Vector2;
        let depth: number;

        if (overlapX < overlapY) {
          normal = { x: diff.x > 0 ? 1 : -1, y: 0 };
          depth = overlapX;
        } else {
          normal = { x: 0, y: diff.y > 0 ? 1 : -1 };
          depth = overlapY;
        }

        const point = { x: centerA.x + normal.x * (colliderA.width / 2), y: centerA.y + normal.y * (colliderA.height / 2) };

        return { bodyA, bodyB, normal, depth, point };
      }
    }

    // Circle vs Box
    if (
      (colliderA.type === ColliderType.Circle && colliderB.type === ColliderType.Box) ||
      (colliderA.type === ColliderType.Box && colliderB.type === ColliderType.Circle)
    ) {
      const circleBody = colliderA.type === ColliderType.Circle ? bodyA : bodyB;
      const boxBody = colliderA.type === ColliderType.Box ? bodyA : bodyB;
      const circle = circleBody.collider as CircleCollider;
      const box = boxBody.collider as BoxCollider;

      const circlePos = Vec2.add(circleBody.position, circle.offset);
      const boxPos = Vec2.add(boxBody.position, box.offset);

      const rect: Rectangle = { x: boxPos.x, y: boxPos.y, width: box.width, height: box.height };

      // Find closest point on box to circle center
      const closestX = Math.max(rect.x, Math.min(circlePos.x, rect.x + rect.width));
      const closestY = Math.max(rect.y, Math.min(circlePos.y, rect.y + rect.height));

      const dx = circlePos.x - closestX;
      const dy = circlePos.y - closestY;
      const distSquared = dx * dx + dy * dy;
      const radiusSquared = circle.radius * circle.radius;

      // Check if circle center is inside the box
      const centerInsideBox = 
        circlePos.x >= rect.x && circlePos.x <= rect.x + rect.width &&
        circlePos.y >= rect.y && circlePos.y <= rect.y + rect.height;

      if (centerInsideBox) {
        // Circle center is inside box - find shortest escape direction
        const leftDist = circlePos.x - rect.x;
        const rightDist = (rect.x + rect.width) - circlePos.x;
        const topDist = circlePos.y - rect.y;
        const bottomDist = (rect.y + rect.height) - circlePos.y;
        
        const minDist = Math.min(leftDist, rightDist, topDist, bottomDist);
        let normal: Vector2;
        let depth: number;
        
        // Push OUT of the box (normal points away from box center)
        if (minDist === leftDist) {
          normal = { x: -1, y: 0 };
          depth = leftDist + circle.radius;
        } else if (minDist === rightDist) {
          normal = { x: 1, y: 0 };
          depth = rightDist + circle.radius;
        } else if (minDist === topDist) {
          normal = { x: 0, y: -1 };
          depth = topDist + circle.radius;
        } else {
          normal = { x: 0, y: 1 };
          depth = bottomDist + circle.radius;
        }
        
        return {
          bodyA: circleBody,
          bodyB: boxBody,
          normal,
          depth,
          point: { x: closestX, y: closestY },
        };
      } else if (distSquared < radiusSquared) {
        // Circle overlaps box but center is outside
        const dist = Math.sqrt(distSquared);
        const depth = circle.radius - dist;
        
        // Normal points from closest point to circle center (pushing circle away)
        const normal = dist > 0.0001 
          ? { x: dx / dist, y: dy / dist }
          : { x: 1, y: 0 }; // Fallback
        
        return {
          bodyA: circleBody,
          bodyB: boxBody,
          normal,
          depth,
          point: { x: closestX, y: closestY },
        };
      }
    }

    return null;
  }

  resolveCollisions(): void {
    // Sort collisions: static-dynamic first (walls), then dynamic-dynamic
    // This ensures walls are resolved before player-player pushes
    const sortedCollisions = [...this.collisions].sort((a, b) => {
      const aHasStatic = a.bodyA.isStatic || a.bodyB.isStatic;
      const bHasStatic = b.bodyA.isStatic || b.bodyB.isStatic;
      if (aHasStatic && !bHasStatic) return -1;
      if (!aHasStatic && bHasStatic) return 1;
      return 0;
    });
    
    for (const collision of sortedCollisions) {
      const { bodyA, bodyB, normal, depth } = collision;

      // Skip triggers
      if (bodyA.isTrigger || bodyB.isTrigger) continue;
      
      // Skip if depth is invalid
      if (depth <= 0 || !isFinite(depth)) continue;

      // Normal points from bodyB toward bodyA
      // So to separate: bodyA moves in +normal direction, bodyB moves in -normal direction
      
      if (bodyA.isStatic && !bodyB.isStatic) {
        // bodyA is static (wall), push bodyB away (in -normal direction)
        bodyB.position.x -= normal.x * depth;
        bodyB.position.y -= normal.y * depth;
        // Stop velocity toward the wall
        const velDotNormal = bodyB.velocity.x * normal.x + bodyB.velocity.y * normal.y;
        if (velDotNormal > 0) {
          bodyB.velocity.x -= normal.x * velDotNormal;
          bodyB.velocity.y -= normal.y * velDotNormal;
        }
      } else if (bodyB.isStatic && !bodyA.isStatic) {
        // bodyB is static (wall), push bodyA away (in +normal direction)
        bodyA.position.x += normal.x * depth;
        bodyA.position.y += normal.y * depth;
        // Stop velocity toward the wall
        const velDotNormal = bodyA.velocity.x * normal.x + bodyA.velocity.y * normal.y;
        if (velDotNormal < 0) {
          bodyA.velocity.x -= normal.x * velDotNormal;
          bodyA.velocity.y -= normal.y * velDotNormal;
        }
      } else if (!bodyA.isStatic && !bodyB.isStatic) {
        // Both dynamic (player-player collision)
        // Use smaller separation to reduce wall-clipping risk
        const halfDepth = depth / 2;
        
        // Before moving, check if pushing into a wall (using velocity as indicator)
        // Only separate if there's overlap, with reduced force
        const separationScale = 0.8; // Don't fully separate - let next iteration handle it
        bodyA.position.x += normal.x * halfDepth * separationScale;
        bodyA.position.y += normal.y * halfDepth * separationScale;
        bodyB.position.x -= normal.x * halfDepth * separationScale;
        bodyB.position.y -= normal.y * halfDepth * separationScale;
      }

      // Update spatial hash
      this.spatialHash.update(bodyA);
      this.spatialHash.update(bodyB);
    }
  }

  // Raycast
  raycast(
    origin: Vector2,
    direction: Vector2,
    maxDistance: number,
    layerMask: number = CollisionLayers.ALL
  ): { body: CollisionBody; point: Vector2; distance: number } | null {
    const normalizedDir = Vec2.normalize(direction);
    const end = Vec2.add(origin, Vec2.mul(normalizedDir, maxDistance));

    let closest: { body: CollisionBody; point: Vector2; distance: number } | null = null;

    for (const [, body] of this.bodies) {
      if ((body.layer & layerMask) === 0) continue;

      const collider = body.collider;
      const pos = Vec2.add(body.position, collider.offset);

      let hitPoint: Vector2 | null = null;

      if (collider.type === ColliderType.Box) {
        const rect: Rectangle = { x: pos.x, y: pos.y, width: collider.width, height: collider.height };
        hitPoint = Collision.lineRectIntersect(origin, end, rect);
      } else if (collider.type === ColliderType.Circle) {
        // Line-circle intersection
        hitPoint = this.lineCircleIntersection(origin, end, pos, collider.radius);
      }

      if (hitPoint) {
        const distance = Vec2.distance(origin, hitPoint);
        if (!closest || distance < closest.distance) {
          closest = { body, point: hitPoint, distance };
        }
      }
    }

    return closest;
  }

  private lineCircleIntersection(
    start: Vector2,
    end: Vector2,
    center: Vector2,
    radius: number
  ): Vector2 | null {
    const d = Vec2.sub(end, start);
    const f = Vec2.sub(start, center);

    const a = Vec2.dot(d, d);
    const b = 2 * Vec2.dot(f, d);
    const c = Vec2.dot(f, f) - radius * radius;

    let discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return null;

    discriminant = Math.sqrt(discriminant);
    const t1 = (-b - discriminant) / (2 * a);
    const t2 = (-b + discriminant) / (2 * a);

    if (t1 >= 0 && t1 <= 1) {
      return Vec2.add(start, Vec2.mul(d, t1));
    }
    if (t2 >= 0 && t2 <= 1) {
      return Vec2.add(start, Vec2.mul(d, t2));
    }

    return null;
  }

  getCollisions(): CollisionResult[] {
    return this.collisions;
  }

  clear(): void {
    this.bodies.clear();
    this.spatialHash.clear();
    this.collisions = [];
  }
}
